import React, { useMemo } from 'react';
import { format } from 'date-fns';
import {
  Drawer,
  Button,
  ButtonType
} from 'syngenta-digital-cropwise-react-ui-kit';
import CloseIcon from 'assets/close.svg';
import OpenInNewIcon from 'assets/open_in_new.svg?react';
import formatNumber, { formatCurrency } from 'helpers/formatNumber';
import { getAbbreviatedUnit } from 'helpers/unitsAbbreviation';
import formatRegistrationNumber from 'helpers/formatRegistrationNumber';
import { capitalizeName } from 'helpers/textHelpers';
import ProductTypeDisplay from 'screens/ProductList/helpers/ProductTypeDisplay';
import useInventoryDocumentationData from 'screens/ProductList/ProductDetails/hooks/useInventoryDocumentationData';
import { PlanProductLineItem } from '../types';
import './PlanProductViewMoreDrawer.css';

const EMPTY_VALUE = '---';
const BORDER_COLOR = '#DFE2E7';
const LABEL_COLOR = '#696F88';
const ICON_COLOR = '#14803C';

export interface PlanProductViewMoreDrawerProps {
  open: boolean;
  onClose: () => void;
  product: PlanProductLineItem | null;
}

interface DetailRowProps {
  label: string;
  value?: string | number | null;
  children?: React.ReactNode;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value, children }) => (
  <div
    className="flex pt-3 pb-2 border-b"
    style={{ borderColor: BORDER_COLOR }}
  >
    <span className="text-sm w-40 shrink-0" style={{ color: LABEL_COLOR }}>
      {label}
    </span>
    {children ?? (
      <span className="text-sm text-neutral-90 font-normal flex-1 wrap-break-word">
        {value != null && value !== '' ? value : EMPTY_VALUE}
      </span>
    )}
  </div>
);

const formatValue = (
  value: number | null | undefined,
  unit?: string,
  suffix = ''
): string =>
  value != null
    ? `${formatNumber(value, getAbbreviatedUnit(unit))}${suffix}`
    : EMPTY_VALUE;

interface DrawerContentProps {
  product: PlanProductLineItem;
  onClose: () => void;
}

/**
 * Split out from the drawer shell so `useInventoryDocumentationData` (a
 * per-`productId` fetch) only ever mounts once a real product is selected —
 * mirrors Applications/Work Orders' own `ProductViewMoreDetailsDrawer`,
 * which only renders its `Content` inside `{product && (...)}` for the same
 * reason.
 */
const DrawerContent: React.FC<DrawerContentProps> = ({ product, onClose }) => {
  // FR-082 (2026-08-24 correction): not every product carries a label
  // document, but some do (confirmed via a real catalog product) — this
  // mirrors `ProductQuantitySection`'s own label-document lookup exactly,
  // since Plans' product entries carry no pre-resolved
  // `productLabelDocumentId` field the way Applications/Work Orders'
  // dedicated `.../products` endpoint denormalizes one.
  const {
    dataDocumentation,
    isLoading: isLoadingDocument,
    openFile
  } = useInventoryDocumentationData(product.productId);
  const documentDescriptions = (dataDocumentation as any)?.documentdescriptions;
  const labelDocument = useMemo(() => {
    if (!Array.isArray(documentDescriptions)) return null;
    return (
      documentDescriptions.find((doc: any) => doc.description === 'Label') ||
      null
    );
  }, [documentDescriptions]);

  return (
    <div className="h-full px-8">
      <div className="relative">
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute top-8 right-0"
        >
          <img alt="close" src={CloseIcon} width={18} height={18} />
        </button>
        <div>
          <h2 className="text-xl font-semibold">{product.productName}</h2>
          <p className="text-neutral-60 text-sm mt-1">
            {product.manufacturerName}
          </p>
        </div>
      </div>
      <div className="mt-6">
        <DetailRow label="Product Type">
          <div className="flex items-center flex-1">
            <ProductTypeDisplay
              productType={capitalizeName(product.producttype)}
              iconSize={18}
            />
          </div>
        </DetailRow>
        <DetailRow
          label="Standard Unit"
          value={capitalizeName(product.stdunit)}
        />
        <DetailRow
          label="EPA Number"
          value={formatRegistrationNumber(product.registrationnumber)}
        />
        <DetailRow
          label="Rate per Acre"
          value={formatValue(
            product.productRateValue,
            product.productRateUnit,
            '/ac'
          )}
        />
        <DetailRow
          label="Total Product"
          value={formatValue(
            product.totalProductValue,
            product.totalProductUnit
          )}
        />
        <DetailRow label="Applied Acres">
          <div className="flex-1">
            <div className="text-sm text-neutral-90 font-normal">
              {formatValue(product.areaValue, 'acre')}
            </div>
            {!!product.coveragePercent && (
              <div className="text-sm text-neutral-90 font-normal">
                {`${product.coveragePercent.toFixed(2)}%`}
              </div>
            )}
          </div>
        </DetailRow>
        <DetailRow
          label="Price per Unit"
          value={
            Number(product.specificCostValue) && product.specificCostUnit
              ? `${formatCurrency(
                  product.specificCostValue
                )}/${getAbbreviatedUnit(product.specificCostUnit)}`
              : EMPTY_VALUE
          }
        />
        <DetailRow
          label="Total Cost"
          value={
            Number(product.totalProductCost)
              ? formatCurrency(product.totalProductCost)
              : EMPTY_VALUE
          }
        />
        <DetailRow label="Timing" value={product.timingEvent} />
        <DetailRow
          label="Number of Applications"
          value={product.applicationCount}
        />
        <DetailRow
          label="Target Date"
          value={
            product.targetDateEpoch
              ? format(product.targetDateEpoch, 'MMM d, yyyy')
              : EMPTY_VALUE
          }
        />
      </div>

      {labelDocument && (
        <div className="w-full mt-6">
          <Button
            type={ButtonType.primary}
            ghost
            onClick={() => openFile(labelDocument.id)}
            icon={<OpenInNewIcon style={{ color: ICON_COLOR }} />}
            block
            className="plan-product-label-button"
            loading={isLoadingDocument}
          >
            Open Product Label
          </Button>
        </div>
      )}
    </div>
  );
};

/**
 * Plans-scoped "View More" drawer (FR-082) — modeled on Applications/Work
 * Orders' own `ProductViewMoreDetailsDrawer`, minus tank/target-pest/
 * application-method fields Plans has no equivalent for. Self-contained
 * rather than reusing that drawer's `Header`/`Content`/`Footer`
 * sub-components directly, matching `PlanProductsTab`'s own precedent of not
 * coupling to Applications/Work Orders' tank-info-shaped Products tab.
 */
const PlanProductViewMoreDrawer: React.FC<PlanProductViewMoreDrawerProps> = ({
  open,
  onClose,
  product
}) => (
  <Drawer
    placement="right"
    className="plan-product-view-more-details-drawer"
    open={open}
    onClose={onClose}
    closable={false}
    maskClosable
    width={424}
    footer={
      <div className="flex justify-start px-8 py-4">
        <Button type={ButtonType.outline} onClick={onClose}>
          &lt; Back
        </Button>
      </div>
    }
  >
    {product && <DrawerContent product={product} onClose={onClose} />}
  </Drawer>
);

export default PlanProductViewMoreDrawer;
